import { createContext, useContext, useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useAuth } from "./AuthContext";
import { userService } from '../apiClient';

// KEEP IN SYNC with the FOUC-prevention inline script in React/public/index.html
// — that script runs before the bundle loads and can't import this file, so it
// re-implements this storage key and resolution logic by hand.
const ThemeContext = createContext();
const STORAGE_KEY = 'fielder.theme';
const OVERRIDE_KEY = 'fielder.theme.overridden';
const DARK_QUERY = '(prefers-color-scheme: dark)';

function getStoredTheme() {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored === 'light' || stored === 'dark' ? stored : 'system';
}

// Safari < 14 / older WebKit only supports the legacy addListener API.
function getMatchMedia() {
  const media = window.matchMedia(DARK_QUERY);
  if (typeof media.addEventListener !== 'function') {
    media.addEventListener = (_, listener) => media.addListener(listener);
    media.removeEventListener = (_, listener) => media.removeListener(listener);
  }
  return media;
}

export function useTheme() {
  return useContext(ThemeContext);
}

export function ThemeProvider({ children }) {
  const { currentUser, currentUserObject } = useAuth();
  const [theme, setThemeState] = useState(getStoredTheme);
  const [systemIsDark, setSystemIsDark] = useState(
    () => window.matchMedia(DARK_QUERY).matches
  );
  // Once the user manually picks a theme, that choice is authoritative on
  // this device — a DB read (a stale write racing a fetch, or a change made
  // on another device) never silently overwrites it again until the user
  // logs out. Backed by localStorage (not just an in-memory ref) so the
  // guarantee survives a page reload — otherwise a theme picked right
  // before a failed save (or before the DB read that would confirm it)
  // gets silently reverted on the very next reload with no explanation.
  const localOverride = useRef(localStorage.getItem(OVERRIDE_KEY) === 'true');

  useEffect(() => {
    const media = getMatchMedia();
    const listener = (e) => setSystemIsDark(e.matches);
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, []);

  const resolvedTheme = theme === 'system' ? (systemIsDark ? 'dark' : 'light') : theme;

  useEffect(() => {
    document.documentElement.setAttribute('data-bs-theme', resolvedTheme);
  }, [resolvedTheme]);

  // The DB value is the source of truth until the user overrides it locally.
  // This re-syncs on every profile refresh (not just once) so a theme change
  // made on another device reaches this tab the next time the profile is
  // re-fetched (e.g. after editing name/phone). It also guards against a
  // stale profile fetch for a different (just-logged-out) user landing after
  // currentUser has already moved on to the next signed-in user.
  useEffect(() => {
    if (localOverride.current) return;
    if (currentUserObject && currentUser && currentUserObject.getUserID?.() !== currentUser.uid) return;
    const dbTheme = currentUserObject?.getThemePreference?.();
    if (!dbTheme || dbTheme === theme) return;
    setThemeState(dbTheme);
    localStorage.setItem(STORAGE_KEY, dbTheme);
  }, [currentUserObject, currentUser, theme]);

  useEffect(() => {
    if (!currentUser) {
      localOverride.current = false;
      localStorage.removeItem(OVERRIDE_KEY);
    }
  }, [currentUser]);

  const setTheme = useCallback((next) => {
    localOverride.current = true;
    localStorage.setItem(OVERRIDE_KEY, 'true');
    setThemeState(next);
    localStorage.setItem(STORAGE_KEY, next);
    if (currentUser) {
      return userService.updateProfile(currentUser.uid, { themePreference: next });
    }
    return Promise.resolve();
  }, [currentUser]);

  const value = useMemo(
    () => ({ theme, resolvedTheme, setTheme }),
    [theme, resolvedTheme, setTheme]
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}
